import type { ReactElement, ReactNode, SyntheticEvent } from "react"
import { useState } from "react"
import type { CtaElement } from "@gatsby-plugin-generic-page/fragments/ctaElement"
import { getCtaButtonFlatData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { getStaticPrices } from "@gatsby-plugin-generic-page/helpers/staticPrices"
import useProductBoxTitleWithLabels from "@gatsby-plugin-generic-page/hooks/useProductBoxTitleWithLabels"
import { CardTariff } from "@ionos-web-design-system/react"
import FormModal from "~/components/FormModal"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { StaticPrice } from "~/fragments/staticPrice"
import isEmpty from "~/lib/isEmpty"
import type { Forms } from "~/types/squidex"
import { getDetailedStaticPriceData } from "../DetailedStaticPrice/helpers/getDetailedStaticPriceData"
import type { ProductType } from "."
import ProductBoxButton from "./ProductBoxButton"
import ProductBoxSlimFooter from "./ProductBoxFooter"

export type ProductBoxWithCtaFormProps = {
  product: ProductType
  cta: CtaElement[]
  form: Forms[]
  staticPrices?: StaticPrice[]
}

export default function ProductBoxWithCtaForm({
  product,
  cta,
  form,
  staticPrices,
}: ProductBoxWithCtaFormProps): ReactElement<ProductBoxWithCtaFormProps> {
  const { currentPriceType } = usePriceTypeContext()

  const titleWithLabels = useProductBoxTitleWithLabels(
    product.title,
    product.discountLabel,
    product.labels
  )

  const prices = staticPrices
    ? getStaticPrices(staticPrices, currentPriceType)
    : {}

  const { text } = getCtaButtonFlatData(cta[0], currentPriceType)

  const [isVisible, setIsVisible] = useState(false)

  const handleFormButton = (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()

    setIsVisible(true)
  }

  const period =
    Object.keys(prices).length === 1
      ? Object.keys(prices)[0]
      : product.period || Object.keys(prices)[0]

  const staticPriceResult =
    !isEmpty(staticPrices) && period
      ? getDetailedStaticPriceData(
          staticPrices,
          period,
          currentPriceType,
          product.additionalText,
          undefined,
          true
        )
      : null

  const buttonNode: ReactNode = (
    <ProductBoxButton
      concept={product.concept}
      variant={product.variant}
      onClick={handleFormButton}
    >
      {text}
    </ProductBoxButton>
  )

  return (
    <>
      <CardTariff
        title={titleWithLabels}
        subtitle={product.description}
        badgeText={product.promoText}
        priceData={
          !isEmpty(staticPriceResult) && !isEmpty(staticPriceResult.data)
            ? { ...staticPriceResult.data, reservePrelineSpace: true }
            : undefined
        }
        button={buttonNode}
        className="leading-normal"
      >
        <div className="flex flex-col gap-gap-8 h-full">
          {product.content.parameters}

          {!isEmpty(product.content?.footer) ? (
            <ProductBoxSlimFooter
              {...product.content.footer}
              setIsChecked={() => {}}
              isChecked={false}
            />
          ) : null}
        </div>
      </CardTariff>

      {isVisible ? (
        <FormModal
          form={form}
          onCloseButtonClick={() => {
            setIsVisible(false)
          }}
        />
      ) : null}
    </>
  )
}
